import { Transition } from '@headlessui/react';
import * as ScrollArea from '@radix-ui/react-scroll-area';
import { type ElementType, forwardRef, type MouseEvent, useRef, useState } from 'react';
import { matchPath, useLocation } from 'react-router';

import { Icon, Link, Popover, Tooltip, usePanelContext } from '~/components/granular';
import useTruncated from '~/hooks/useTruncated';
import { classNames } from '~/utils/styles';
import type {
  NavigationFooterLinkProps,
  NavigationFooterProps,
  NavigationLinkProps,
  NavigationProps,
} from './Navigation.types';

export function Navigation({ navItems, isCollapsed = false, className }: NavigationProps) {
  return (
    <ScrollArea.Root className={classNames('w-full overflow-hidden', isCollapsed ? 'mt-4' : 'mt-1')}>
      <ScrollArea.Viewport className="h-full w-full">
        <nav className={classNames('max-w-[200px]', className)}>
          {navItems.map((item) => (
            <div className="mb-2 overflow-hidden whitespace-nowrap" key={item.id}>
              <Transition
                as="div"
                className="overflow-hidden whitespace-nowrap"
                enter="transform transition-all duration-300"
                enterFrom="opacity-0 h-0"
                enterTo="opacity-100 h-[32px]"
                leave="transform duration-0 transition-all ease-in-out "
                leaveFrom="opacity-100 h-[32px]"
                leaveTo="opacity-0 h-0"
                show={!isCollapsed}
              >
                <div className="label-300 px-4 py-2 text-neutral-600">{item.label}</div>
              </Transition>
              {item.links.map((child) => (
                <NavigationLinkWithTooltip
                  key={child.id}
                  href={child.href}
                  icon={child.icon}
                  isActive={child.isActive}
                  isCollapsed={isCollapsed}
                  label={child.label}
                  link={child.link}
                  links={child.links}
                  onClick={child.onClick}
                  target={child.target}
                  to={child.to}
                />
              ))}
            </div>
          ))}
        </nav>
      </ScrollArea.Viewport>
      <ScrollArea.Scrollbar
        className="flex touch-none bg-neutral-200 p-0.5 transition-colors duration-160 ease-out select-none hover:bg-neutral-300 data-[orientation=horizontal]:h-2.5 data-[orientation=horizontal]:flex-col data-[orientation=vertical]:w-2.5 data-[state=hidden]:opacity-0 data-[state=visible]:opacity-100"
        orientation="vertical"
      >
        <ScrollArea.Thumb className="relative flex-1 rounded-[10px] bg-neutral-500 before:absolute before:top-1/2 before:left-1/2 before:h-full before:min-h-[44px] before:w-full before:min-w-[44px] before:-translate-x-1/2 before:-translate-y-1/2 before:content-[''] hover:bg-neutral-600" />
      </ScrollArea.Scrollbar>
    </ScrollArea.Root>
  );
}

export const NavigationLink = forwardRef<HTMLAnchorElement, NavigationLinkProps>(
  (
    {
      className,
      link = 'a',
      links,
      icon,
      isActive = false,
      label,
      isCollapsed = false,
      withArrow = false,
      textRef,
      ...props
    },
    forwardedRef,
  ) => {
    const NavLink = link as ElementType;

    const { onToggleCollapse } = usePanelContext();

    const childLinks = links?.filter(({ isHidden }) => !isHidden);

    const { pathname } = useLocation();

    // Match against all links (including hidden detail/edit routes) so the parent
    // stays highlighted on nested pages. Dynamic `to` builders are invoked with a
    // `:id` placeholder to produce a route pattern `matchPath` can match against,
    // e.g. `/certificate-transactions/:id/details`.
    const isAnyLinkActive = !!links?.some((link) => {
      if (link.href) {
        return pathname.includes(link.href);
      }
      const linkPattern = typeof link.to === 'function' ? link.to(':id') : link.to;
      return !!linkPattern && Boolean(matchPath({ end: true, path: linkPattern }, pathname));
    });

    const hasVisibleChildren = !!childLinks?.length;

    const [linksExpanded, setLinksExpanded] = useState(isAnyLinkActive);

    function handleItemClick(event: React.MouseEvent<HTMLAnchorElement>) {
      if (childLinks?.length) {
        if (isCollapsed) {
          onToggleCollapse();
          setLinksExpanded(true);
        } else {
          setLinksExpanded(!linksExpanded);
        }
      }
      props.onClick?.(event);
    }

    return (
      <>
        <NavLink
          className={classNames(
            'label-300 flex cursor-pointer items-center gap-2 overflow-hidden py-[6px] text-ellipsis text-neutral-1000 hover:bg-neutral-200 aria-current:bg-teal-300 aria-current:text-teal-700 [&_svg]:text-neutral-600 aria-current:[&_svg]:text-teal-700',
            isCollapsed ? 'px-5' : 'px-4',
            {
              'bg-teal-300 text-teal-700 [&_svg]:text-teal-700':
                isActive || (isAnyLinkActive && (!hasVisibleChildren || !linksExpanded || isCollapsed)),
            },
            className,
          )}
          href={props.href}
          onClick={handleItemClick}
          ref={forwardedRef}
          to={props.to}
        >
          {icon && (
            <span>
              <Icon filled={false} height={16} icon={icon} width={16} />
            </span>
          )}
          <Transition
            as="span"
            className="contents"
            enter="transform transition-opacity duration-300"
            enterFrom="opacity-0 "
            enterTo="opacity-100"
            leave="transform duration-0 transition-opacity ease-in-out"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
            show={!isCollapsed}
          >
            <span className="truncate" ref={textRef}>
              {label}
            </span>

            {withArrow && (
              <Icon className="ml-auto text-neutral-600" filled={true} height={16} icon="ArrowRightSIcon" width={16} />
            )}
            {!!childLinks?.length && (
              <Icon
                className="ml-auto text-neutral-600"
                filled={true}
                height={16}
                icon={linksExpanded ? 'ArrowUpSIcon' : 'ArrowDownSIcon'}
                width={16}
              />
            )}
          </Transition>
        </NavLink>

        {!isCollapsed && !!childLinks?.length && (
          <Transition
            as="div"
            className="overflow-hidden whitespace-nowrap"
            enter="transform transition-all duration-300"
            enterFrom="opacity-0 h-0"
            enterTo="opacity-100 h-[56px]"
            leave="transform duration-300 transition-all ease-in-out "
            leaveFrom="opacity-100 h-[56px]"
            leaveTo="opacity-0 h-0"
            show={linksExpanded}
          >
            {links?.map((child) => (
              <NavigationLink
                className="pl-10"
                href={child.href}
                icon={child.icon}
                isActive={child.isActive}
                isCollapsed={isCollapsed}
                key={child.id}
                label={child.label}
                link={child.link}
                links={child.links}
                onClick={child.onClick}
                to={child.to}
              />
            ))}
          </Transition>
        )}
      </>
    );
  },
);

export const NavigationLinkWithTooltip = forwardRef<HTMLAnchorElement, NavigationLinkProps>(
  ({ link = 'a', links, icon, label, isCollapsed = false, withArrow = false, target, ...props }, forwardedRef) => {
    const textRef = useRef<HTMLElement>(null);
    const isTruncated = useTruncated(textRef);
    const [isTooltipOpen, setIsTooltipOpen] = useState(false);

    function handleTooltipOpenChange(open: boolean) {
      if (!isCollapsed && !isTruncated) {
        return;
      }

      setIsTooltipOpen(open);
    }

    return (
      <Tooltip
        delayDuration={isCollapsed ? 0 : undefined}
        disabled={!isCollapsed && !isTruncated}
        onOpenChange={handleTooltipOpenChange}
        open={isTooltipOpen}
        side="right"
        trigger={
          <div>
            <NavigationLink
              href={props.href}
              icon={icon}
              isActive={props.isActive}
              isCollapsed={isCollapsed}
              label={label}
              link={link}
              links={links}
              onClick={props.onClick}
              ref={forwardedRef}
              target={target}
              textRef={textRef}
              to={props.to}
              withArrow={withArrow}
            />
          </div>
        }
      >
        {label}
      </Tooltip>
    );
  },
);

export function NavigationFooter({ className, navItems, isCollapsed = false }: NavigationFooterProps) {
  return (
    <div className={classNames(className)}>
      {navItems.map((item) => (
        <FooterNavigationLinkWithPopover
          icon={item.icon || 'SettingsIcon'}
          isCollapsed={isCollapsed}
          key={item.id}
          label={item.label}
          links={item.links}
        />
      ))}
    </div>
  );
}

export function FooterNavigationLinkWithPopover({
  icon,
  label,
  links,
  link,
  isCollapsed = false,
}: NavigationFooterLinkProps) {
  const [popoverOpen, setPopoverOpen] = useState(false);

  const linkClassName = 'px-4 py-[10px] hover:bg-neutral-100 hover:no-underline [&_svg]:-my-0.5 [&_svg]:size-5';
  return (
    <Popover
      className="min-w-[120px] p-1"
      direction="right"
      onOpenAutoFocus={(e) => e?.preventDefault()}
      onTriggerClick={(open) => setPopoverOpen(open)}
      open={popoverOpen}
      trigger={<NavigationLinkWithTooltip icon={icon} isCollapsed={isCollapsed} label={label} link={link} withArrow />}
    >
      <div className="flex flex-col">
        {links
          .filter(({ isHidden }) => !isHidden)
          .map((link) => {
            // isHidden needs to be removed from the DOM element
            const { isHidden: _, ...linkProps } = link;
            return link.component ? (
              <link.component
                className={linkClassName}
                key={link.id}
                label={link.label}
                setPopoverOpen={setPopoverOpen}
              />
            ) : (
              <Link
                {...linkProps}
                appearance="secondary"
                as={link.link}
                className={linkClassName}
                key={link.id}
                onClick={(e: MouseEvent<HTMLAnchorElement>) => {
                  link.onClick?.(e);
                  setPopoverOpen(false);
                }}
                size="sm"
              >
                {link.label}
              </Link>
            );
          })}
      </div>
    </Popover>
  );
}
